/**
 * <ns-site-header> — behaviour for sections/ns-site-header.liquid.
 *
 * Desktop (>= 1150px): a panel opens on hover or keyboard focus of its top-level
 * item and closes when the pointer leaves the item + panel (after a short grace
 * period so a diagonal move into the panel does not drop it), on Escape, or on a
 * click on the dimmed page. On a touch device at desktop width the first tap on
 * an item opens its panel instead of following the link; a second tap follows it.
 *
 * Tabs: `data-nsh-tabs-mode="hover"` (desktop panel) activates on pointer enter
 * and focus, so the tab link stays clickable; `"click"` (mobile drawer) activates
 * on tap.
 *
 * Mobile (< 1150px): a native <dialog> drawer (focus handling, Escape and the
 * inert page come from the platform). It slides in (CSS on [open]) and slides
 * out before it closes (.is-closing, held until the exit animation ends). Only
 * one accordion is open at a time; accordions animate their height, and their
 * rows rise in one after another, as the desktop panels do.
 * `html.lock` is the theme's existing scroll-lock class.
 *
 * Panels and the overlay are shown with an `is-open` class rather than the
 * `hidden` attribute, so CSS can transition them (visibility keeps a closed
 * panel out of the tab order and the accessibility tree).
 */
if (!customElements.get('ns-site-header')) {
  const DESKTOP_QUERY = '(min-width: 1150px)';
  const CLOSE_DELAY_MS = 150;
  const ACCORDION_MS = 300;
  const DRAWER_CLOSE_MS = 250;
  const DRAWER_ITEM_STEP_MS = 40; // keep in step with .ns-sh__drawer-item in the CSS
  const DRAWER_ITEM_BASE_MS = 120;
  const REVEAL_MS = 350;
  const REVEAL_STEP_MS = 35;
  const REVEAL_SELECTOR =
    '.ns-sh__row, .ns-sh__review, .ns-sh__card, .ns-sh__tablist, .ns-sh__heading-link, .ns-sh__shop-link, .ns-sh__quick-links';
  const EASE_OUT = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
  const RICHPANEL_LINK = 'a[href^="#rp-customer-widget-"]';
  const RICHPANEL_FRAME_ID = 'richpanel_messenger_iframe';
  const RICHPANEL_LOAD_TIMEOUT_MS = 10000;
  // Richpanel runs its own digest 1s after MESSENGER_LOADED; a route set
  // before that is dropped.
  const RICHPANEL_SETTLE_MS = 1100;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  customElements.define(
    'ns-site-header',
    class NsSiteHeader extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
        this.items = Array.from(this.querySelectorAll('[data-nsh-item]'));
        this.overlay = this.querySelector('[data-nsh-overlay]');
        this.drawer = this.querySelector('[data-nsh-drawer]');
        this.drawerOpenButton = this.querySelector('[data-nsh-drawer-open]');
        this.openItem = null;
        this.closeTimer = null;

        this.items.forEach((item) => this.bindItem(item, signal));
        this.querySelectorAll('[data-nsh-tabs]').forEach((tabs) => this.bindTabs(tabs, signal));
        this.bindDrawer(signal);

        this.overlay?.addEventListener('click', () => this.closePanel(), { signal });
        document.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Escape' && this.openItem) {
              const trigger = this.openItem.querySelector('[data-nsh-trigger]');
              this.closePanel();
              trigger?.focus();
            }
          },
          { signal },
        );
        this.desktopQuery.addEventListener(
          'change',
          () => {
            this.closePanel();
            this.closeDrawer({ immediate: true });
          },
          { signal },
        );

        // Richpanel links (#rp-customer-widget-faq-…, -prechat_form). Richpanel
        // opens its messenger on hashchange but loads it lazily, and until it
        // has loaded a link only opens the home screen and drops the route (a
        // second click works). So a click that beats the load is remembered and
        // its hash re-applied once the messenger reports MESSENGER_LOADED.
        this.richpanelReady = Boolean(document.getElementById(RICHPANEL_FRAME_ID));
        window.addEventListener('message', (event) => this.onRichpanelMessage(event), { signal });
        this.addEventListener(
          'click',
          (event) => {
            const link = event.target.closest(RICHPANEL_LINK);
            if (link && !this.richpanelReady) this.queueRichpanelHash(link.getAttribute('href'));
          },
          { signal },
        );

        if (window.Shopify?.designMode) this.bindEditor(signal);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        window.clearTimeout(this.closeTimer);
        window.clearTimeout(this.drawerCloseTimer);
        window.clearTimeout(this.richpanelTimer);
        this.onDrawerClosed();
      }

      /* ── Desktop panels ─────────────────────────────────────────────── */

      bindItem(item, signal) {
        const trigger = item.querySelector('[data-nsh-trigger]');
        if (!trigger) return;

        item.addEventListener(
          'pointerenter',
          (event) => {
            if (event.pointerType === 'mouse' && this.desktopQuery.matches) this.openPanel(item);
          },
          { signal },
        );
        item.addEventListener(
          'pointerleave',
          (event) => {
            if (event.pointerType === 'mouse') this.scheduleClose();
          },
          { signal },
        );
        item.addEventListener(
          'focusin',
          () => {
            if (this.desktopQuery.matches) this.openPanel(item);
          },
          { signal },
        );
        item.addEventListener(
          'focusout',
          (event) => {
            if (!item.contains(event.relatedTarget)) this.scheduleClose();
          },
          { signal },
        );

        // Touch at desktop width: the first tap opens the panel, a second tap
        // follows the link. The open state is read at pointerdown, because the
        // tap's own focus event has already opened the panel by click time.
        let tapPointer = null;
        let wasOpenAtTap = false;
        trigger.addEventListener(
          'pointerdown',
          (event) => {
            tapPointer = event.pointerType;
            wasOpenAtTap = this.openItem === item;
          },
          { signal },
        );
        trigger.addEventListener(
          'click',
          (event) => {
            const pointer = tapPointer;
            tapPointer = null;
            if (!this.desktopQuery.matches) return;
            if (trigger.tagName === 'BUTTON') {
              this.openPanel(item);
            } else if (pointer && pointer !== 'mouse' && !wasOpenAtTap) {
              event.preventDefault();
              this.openPanel(item);
            }
          },
          { signal },
        );
      }

      openPanel(item) {
        window.clearTimeout(this.closeTimer);
        if (this.openItem === item) return;
        if (this.openItem) this.setPanelState(this.openItem, false);
        this.setPanelState(item, true);
        this.openItem = item;
        this.overlay?.classList.add('is-open');
      }

      scheduleClose() {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = window.setTimeout(() => this.closePanel(), CLOSE_DELAY_MS);
      }

      closePanel() {
        window.clearTimeout(this.closeTimer);
        if (!this.openItem) return;
        this.setPanelState(this.openItem, false);
        this.openItem = null;
        this.overlay?.classList.remove('is-open');
      }

      setPanelState(item, open) {
        const trigger = item.querySelector('[data-nsh-trigger]');
        const panel = item.querySelector('[data-nsh-panel]');
        trigger?.setAttribute('aria-expanded', String(open));
        panel?.classList.toggle('is-open', open);
      }

      /* ── Tabs ───────────────────────────────────────────────────────── */

      bindTabs(container, signal) {
        const tabs = Array.from(container.querySelectorAll('[data-nsh-tab]'));
        const mode = container.dataset.nshTabsMode;

        tabs.forEach((tab) => {
          if (mode === 'hover') {
            tab.addEventListener('pointerenter', () => this.selectTab(container, tab), { signal });
            tab.addEventListener('focus', () => this.selectTab(container, tab), { signal });
          } else {
            tab.addEventListener(
              'click',
              (event) => {
                event.preventDefault();
                this.selectTab(container, tab);
              },
              { signal },
            );
          }
        });
      }

      selectTab(container, selected) {
        container.querySelectorAll('[data-nsh-tab]').forEach((tab) => {
          const isSelected = tab === selected;
          tab.setAttribute('aria-selected', String(isSelected));
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = !isSelected;
        });
        if (container.dataset.nshTabsMode === 'click') {
          selected.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
      }

      /* ── Mobile drawer ──────────────────────────────────────────────── */

      bindDrawer(signal) {
        if (!this.drawer) return;

        this.drawerOpenButton?.addEventListener('click', () => this.openDrawer(), { signal });
        this.querySelector('[data-nsh-drawer-close]')?.addEventListener('click', () => this.closeDrawer(), {
          signal,
        });
        // Escape closes a modal dialog natively; `cancel` fires first and
        // `close` after. Cleanup runs from both and from closeDrawer(), because
        // `close` is not dispatched reliably in every embedded browser (seen in
        // QA: the dialog closed and the page stayed scroll-locked).
        // Escape: run the exit animation instead of closing at once. If the
        // browser does not let the cancel be prevented, the dialog closes and
        // the 'close' listener below still cleans up.
        this.drawer.addEventListener(
          'cancel',
          (event) => {
            event.preventDefault();
            this.closeDrawer();
          },
          { signal },
        );
        this.drawer.addEventListener('close', () => this.onDrawerClosed(), { signal });

        // An in-page link (e.g. "Chat with us", #rp-customer-widget-prechat_form)
        // opens something on this page. The modal drawer sits in the top layer,
        // above any z-index, so it has to get out of the way.
        this.drawer.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('a[href^="#"]')) this.closeDrawer();
          },
          { signal },
        );

        this.accordions = Array.from(this.drawer.querySelectorAll('[data-nsh-accordion]'));
        this.accordions.forEach((accordion) => {
          accordion.querySelector('summary')?.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              this.toggleAccordion(accordion, !accordion.open);
            },
            { signal },
          );
        });
      }

      toggleAccordion(accordion, open) {
        if (open) {
          this.accordions.forEach((other) => {
            if (other !== accordion && other.open) this.toggleAccordion(other, false);
          });
        }

        const body = accordion.querySelector('.ns-sh__accordion-body');
        accordion.nshAnimation?.cancel();
        if (!body || reducedMotion()) {
          accordion.open = open;
          return;
        }

        const startHeight = accordion.open ? body.offsetHeight : 0;
        if (open) accordion.open = true;
        const endHeight = open ? body.scrollHeight : 0;

        const animation = body.animate(
          [
            { height: `${startHeight}px`, opacity: open ? 0 : 1 },
            { height: `${endHeight}px`, opacity: open ? 1 : 0 },
          ],
          { duration: ACCORDION_MS, easing: EASE_OUT },
        );
        if (open) this.revealRows(accordion, 0);
        accordion.nshAnimation = animation;

        // A page that gets no rendering frames (a throttled or hidden tab) never
        // finishes the animation, which would leave the accordion stuck mid-way.
        // The timer settles the final state regardless.
        const settle = () => {
          window.clearTimeout(accordion.nshSettleTimer);
          if (accordion.nshAnimation === animation) accordion.nshAnimation = null;
          if (!open) accordion.open = false;
        };
        window.clearTimeout(accordion.nshSettleTimer);
        accordion.nshSettleTimer = window.setTimeout(() => {
          if (accordion.nshAnimation !== animation) return;
          animation.cancel();
          settle();
        }, ACCORDION_MS + 150);
        animation.onfinish = settle;
        animation.oncancel = () => {
          window.clearTimeout(accordion.nshSettleTimer);
          if (accordion.nshAnimation === animation) accordion.nshAnimation = null;
        };
      }

      /**
       * Rows inside an accordion rise in one after another. Only elements that
       * are rendered (not in a hidden tab panel) and not nested inside another
       * revealed element take part, so each thing moves once.
       */
      revealRows(accordion, baseDelay) {
        if (reducedMotion()) return;
        const candidates = Array.from(accordion.querySelectorAll(REVEAL_SELECTOR)).filter(
          (element) => element.getClientRects().length > 0,
        );
        const targets = candidates.filter(
          (element) => !candidates.some((other) => other !== element && other.contains(element)),
        );
        targets.forEach((element, index) => {
          element.animate(
            [
              { opacity: 0, transform: 'translateY(8px)' },
              { opacity: 1, transform: 'none' },
            ],
            {
              duration: REVEAL_MS,
              delay: baseDelay + index * REVEAL_STEP_MS + 60,
              easing: EASE_OUT,
              fill: 'backwards',
            },
          );
        });
      }

      openDrawer() {
        if (!this.drawer || this.drawer.open) return;
        this.drawer.showModal();
        // showModal() focuses the first focusable element, the logo link, and
        // Safari paints a focus ring on it. Focus the dialog itself instead;
        // Tab still reaches the logo first.
        this.drawer.focus({ preventScroll: true });
        document.documentElement.classList.add('lock');
        this.drawerLocked = true;
        this.drawerOpenButton?.setAttribute('aria-expanded', 'true');

        // An accordion that starts open (open_in_drawer) reveals its rows once
        // its own drawer item has risen in.
        this.accordions?.forEach((accordion) => {
          if (!accordion.open) return;
          const item = accordion.closest('.ns-sh__drawer-item');
          const itemIndex = Number.parseInt(item?.style.getPropertyValue('--nsh-i'), 10) || 0;
          this.revealRows(accordion, DRAWER_ITEM_BASE_MS + itemIndex * DRAWER_ITEM_STEP_MS + 120);
        });
      }

      closeDrawer({ immediate = false } = {}) {
        const drawer = this.drawer;
        if (!drawer?.open) {
          this.onDrawerClosed();
          return;
        }
        if (immediate || reducedMotion()) {
          this.finishDrawerClose();
          return;
        }
        if (drawer.classList.contains('is-closing')) return;

        drawer.classList.add('is-closing');
        const onEnd = (event) => {
          if (event.target === drawer && event.animationName === 'nsh-drawer-out') this.finishDrawerClose();
        };
        this.drawerAnimationEnd = onEnd;
        drawer.addEventListener('animationend', onEnd);
        // animationend never fires in a hidden tab or when the CSS is missing.
        this.drawerCloseTimer = window.setTimeout(() => this.finishDrawerClose(), DRAWER_CLOSE_MS + 150);
      }

      finishDrawerClose() {
        window.clearTimeout(this.drawerCloseTimer);
        if (this.drawerAnimationEnd) {
          this.drawer?.removeEventListener('animationend', this.drawerAnimationEnd);
          this.drawerAnimationEnd = null;
        }
        this.drawer?.classList.remove('is-closing');
        if (this.drawer?.open) this.drawer.close();
        this.onDrawerClosed();
      }

      onDrawerClosed() {
        this.drawer?.classList.remove('is-closing');
        // Only release a lock this drawer took — the cart drawer shares the class.
        if (!this.drawerLocked) return;
        this.drawerLocked = false;
        document.documentElement.classList.remove('lock');
        this.drawerOpenButton?.setAttribute('aria-expanded', 'false');
      }

      /* ── Theme editor: selecting a block reveals it ─────────────────── */

      onRichpanelMessage(event) {
        const frame = document.getElementById(RICHPANEL_FRAME_ID);
        if (!frame || event.source !== frame.contentWindow) return;
        let data = event.data;
        try {
          data = JSON.parse(data);
        } catch (error) {
          // Richpanel posts both JSON and plain strings; a plain string is used as is.
        }
        if (data !== 'MESSENGER_LOADED') return;

        this.richpanelReady = true;
        const pending = this.richpanelPending;
        this.richpanelPending = null;
        if (!pending || Date.now() - pending.at > RICHPANEL_LOAD_TIMEOUT_MS) return;
        window.clearTimeout(this.richpanelTimer);
        this.richpanelTimer = window.setTimeout(() => {
          // Richpanel only reacts to a hashchange, so clear the hash first.
          history.replaceState(null, '', window.location.pathname + window.location.search);
          window.location.hash = pending.hash;
        }, RICHPANEL_SETTLE_MS);
      }

      queueRichpanelHash(hash) {
        // The click itself still runs: it sets the hash, which makes Richpanel
        // load and open the messenger.
        this.richpanelPending = { hash, at: Date.now() };
      }

      bindEditor(signal) {
        const section = this.closest('.shopify-section');
        if (!section) return;

        section.addEventListener(
          'shopify:block:select',
          (event) => {
            const blockId = event.detail?.blockId;
            if (!blockId) return;
            if (this.desktopQuery.matches) {
              const item = this.items.find((candidate) =>
                candidate.querySelector(`[id="NsSiteHeaderPanel-${blockId}"]`),
              );
              if (item) this.openPanel(item);
            } else {
              const accordion = this.drawer?.querySelector(`[data-block-id="${blockId}"]`);
              this.openDrawer();
              if (accordion) this.toggleAccordion(accordion, true);
            }
          },
          { signal },
        );
        section.addEventListener(
          'shopify:block:deselect',
          () => {
            this.closePanel();
          },
          { signal },
        );
      }
    },
  );
}
